import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  pePortfolioService,
  PePortfolioMembershipDto,
  PePortfolioUserScopeDto,
  PePortfolioWorkspaceDto,
  PePortfolioCompanySummaryDto,
  PePortfolioScopeOptionsDto,
} from '@/services/pePortfolioService';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { PePortfolioChildTenantPanel } from '@/components/pe-portfolio/PePortfolioChildTenantPanel';
import { PePortfolioProvisioningTab } from '@/components/pe-portfolio/PePortfolioProvisioningTab';
import { PePortfolioScopeHierarchyModal } from '@/components/pe-portfolio/PePortfolioScopeHierarchyModal';
import {
  ChartBarIcon,
  InboxArrowDownIcon,
  BuildingOfficeIcon,
  PresentationChartLineIcon,
  UserGroupIcon,
  LinkIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

type Tab = 'child' | 'pending' | 'companies' | 'provisioning' | 'scopes' | 'summary' | 'metrics' | 'connectors';

export const PePortfolioPage: React.FC = () => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const isPeTenant = user?.tenantType === 'PORTFOLIO';
  const isAdmin = user?.isTenantAdmin === true || user?.role === 'Tenant Administrator';

  const [tab, setTab] = useState<Tab>('child');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [workspace, setWorkspace] = useState<PePortfolioWorkspaceDto | null>(null);

  const [pending, setPending] = useState<PePortfolioMembershipDto[]>([]);
  const [companies, setCompanies] = useState<PePortfolioMembershipDto[]>([]);
  const [scheduled, setScheduled] = useState<PePortfolioMembershipDto[]>([]);
  const [summary, setSummary] = useState<{ companies: PePortfolioCompanySummaryDto[] } | null>(null);
  const [connectors, setConnectors] = useState<{ enabled: boolean; message: string } | null>(null);
  const [metricDefs, setMetricDefs] = useState<Array<{ id: string; name: string; code: string; operation: string }>>(
    []
  );
  const [scopeOptions, setScopeOptions] = useState<PePortfolioScopeOptionsDto | null>(null);
  const [scopes, setScopes] = useState<PePortfolioUserScopeDto[]>([]);
  const [scopePeUserId, setScopePeUserId] = useState('');
  const [scopeChildId, setScopeChildId] = useState('');
  const [hierarchyModalScope, setHierarchyModalScope] = useState<PePortfolioUserScopeDto | null>(null);

  const loadPe = useCallback(async () => {
    const ws = await pePortfolioService.getWorkspace();
    setWorkspace(ws);
    if (!ws.viewPortfolio) {
      setPending([]);
      setCompanies([]);
      setScheduled([]);
      setSummary(null);
      setConnectors(null);
      setScopes([]);
      setMetricDefs([]);
      return;
    }
    const [c, sch, sum, conn] = await Promise.all([
      pePortfolioService.listCompanies(),
      pePortfolioService.listScheduledDeletions(),
      pePortfolioService.getConsolidatedSummary(),
      pePortfolioService.getConnectorsStatus(),
    ]);
    setCompanies(c);
    setScheduled(sch);
    setSummary(sum);
    setConnectors(conn);
    if (ws.manageInbound) {
      setPending(await pePortfolioService.listIncoming());
    } else {
      setPending([]);
    }
    if (ws.manageScopes) {
      const [sc, md] = await Promise.all([
        pePortfolioService.listScopes(),
        pePortfolioService.listSyntheticDefinitions(),
      ]);
      setScopes(sc);
      setMetricDefs(md);
      setScopeOptions(await pePortfolioService.getScopeOptions());
    } else {
      setScopes([]);
      setMetricDefs([]);
      setScopeOptions(null);
    }
  }, []);

  useEffect(() => {
    if (user && isPeTenant) {
      setTab('summary');
    } else if (user) {
      setTab('child');
    }
  }, [user, isPeTenant]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    if (!isPeTenant && !isAdmin) {
      setLoading(false);
      return;
    }
    if (!isPeTenant && isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await loadPe();
      } catch (e) {
        if (!cancelled) toast.error(pePortfolioService.errMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, isPeTenant, loadPe]);

  const subtitle = useMemo(
    () => (isPeTenant ? t('pePortfolio.subtitle.peFirm') : t('pePortfolio.subtitle.child')),
    [isPeTenant, t, currentLanguage.id]
  );

  if (!user) {
    return null;
  }

  if (!isPeTenant && !isAdmin) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="border border-[var(--color-border)] rounded-lg p-4">
            <div className="mb-2">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{t('pePortfolio.title')}</h1>
                  <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
                    {t('pePortfolio.accessDenied.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isPeTenant && workspace && !workspace.viewPortfolio) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="border border-[var(--color-border)] rounded-lg p-4">
            <div className="mb-2">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{t('pePortfolio.title')}</h1>
                  <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
                    {t('pePortfolio.noPermission.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const panelClass = 'border border-[var(--color-border)] rounded-lg overflow-hidden';

  /** Prefer API childTenantName; fall back to consolidated summary / scope options (same tenant join as Summary). */
  const portfolioCompanyDisplayName = (m: PePortfolioMembershipDto) => {
    const direct = m.childTenantName?.trim();
    if (direct) return direct;
    const fromSummary = summary?.companies?.find((c) => c.childTenantId === m.childTenantId)?.tenantName?.trim();
    if (fromSummary) return fromSummary;
    const fromScope = scopeOptions?.companies?.find((c) => c.childTenantId === m.childTenantId)?.tenantName?.trim();
    if (fromScope) return fromScope;
    return '—';
  };

  const peTabs = (ws: PePortfolioWorkspaceDto) => {
    const items: { key: Tab; label: string; show: boolean; icon: React.ComponentType<{ className?: string }> }[] = [
      { key: 'summary', label: t('pePortfolio.tabs.summary'), show: ws.viewPortfolio, icon: ChartBarIcon },
      { key: 'pending', label: t('pePortfolio.tabs.incomingRequests'), show: ws.manageInbound, icon: InboxArrowDownIcon },
      { key: 'companies', label: t('pePortfolio.tabs.portfolioCompanies'), show: ws.viewPortfolio, icon: BuildingOfficeIcon },
      { key: 'provisioning', label: t('pePortfolio.tabs.provisioning'), show: ws.provisionToChild, icon: ServerStackIcon },
      { key: 'metrics', label: t('pePortfolio.tabs.syntheticMetrics'), show: ws.manageScopes, icon: PresentationChartLineIcon },
      { key: 'scopes', label: t('pePortfolio.tabs.userAccess'), show: ws.manageScopes, icon: UserGroupIcon },
      { key: 'connectors', label: t('pePortfolio.tabs.connectors'), show: ws.viewPortfolio, icon: LinkIcon },
    ];
    const visible = items.filter((i) => i.show);
    return (
      <div className="mt-6">
        <div className="tab-bar">
          <nav className="flex gap-1 overflow-x-auto items-end" aria-label={t('pePortfolio.aria.tabs')}>
            {visible.map(({ key: k, label, icon: Icon }) => {
              const isActive = tab === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  className={`tab-button inline-flex items-center gap-2 ${isActive ? 'tab-button-selected' : ''}`}
                >
                  <Icon className="h-5 w-5 shrink-0 [stroke:currentColor]" />
                  {label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    );
  };

  const onApprove = async (m: PePortfolioMembershipDto) => {
    try {
      setBusy(true);
      await pePortfolioService.approve(m.id, m.version);
      toast.success(t('pePortfolio.toast.requestApproved'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onReject = async (m: PePortfolioMembershipDto) => {
    try {
      setBusy(true);
      await pePortfolioService.reject(m.id, undefined, m.version);
      toast.success(t('pePortfolio.toast.requestRejected'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (childTenantId: string) => {
    if (!window.confirm(t('pePortfolio.confirm.removeCompany'))) return;
    try {
      setBusy(true);
      await pePortfolioService.removeCompany(childTenantId);
      toast.success(t('pePortfolio.toast.companyRemoved'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSchedule = async (childTenantId: string) => {
    if (!window.confirm(t('pePortfolio.confirm.scheduleDeletion'))) {
      return;
    }
    try {
      setBusy(true);
      await pePortfolioService.scheduleDelete(childTenantId);
      toast.success(t('pePortfolio.toast.deletionScheduled'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onPeRevert = async (childTenantId: string) => {
    try {
      setBusy(true);
      await pePortfolioService.revertDelete(childTenantId);
      toast.success(t('pePortfolio.toast.scheduledDeletionCancelled'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onAssignScope = async () => {
    if (!scopePeUserId.trim() || !scopeChildId.trim()) {
      toast.error(t('pePortfolio.toast.selectUserAndCompany'));
      return;
    }
    try {
      setBusy(true);
      await pePortfolioService.assignScope(scopePeUserId.trim(), scopeChildId.trim());
      setScopePeUserId('');
      setScopeChildId('');
      toast.success(t('pePortfolio.toast.accessAssigned'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onRevokeScope = async (id: string) => {
    try {
      setBusy(true);
      await pePortfolioService.revokeScope(id);
      toast.success(t('pePortfolio.toast.accessRevoked'));
      await loadPe();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const peSection = () => {
    const ws = workspace;
    if (!ws) {
      return (
        <div className="mt-6">
          <p className="text-sm text-[var(--color-secondary-text)]">{t('pePortfolio.loadingWorkspace')}</p>
        </div>
      );
    }
    return (
    <div>
      {peTabs(ws)}
      <div className="mt-6">
      {tab === 'summary' && (
        <div className={panelClass}>
          {(summary?.companies ?? []).length === 0 ? (
            <p className="p-6 md:p-8 text-sm text-[var(--color-secondary-text)]">{t('pePortfolio.summary.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]">
                    <th className="p-3 md:p-4 font-semibold">{t('pePortfolio.summary.col.company')}</th>
                    <th className="p-3 md:p-4 font-semibold">{t('pePortfolio.summary.col.openIssues')}</th>
                    <th className="p-3 md:p-4 font-semibold">{t('pePortfolio.summary.col.openActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.companies ?? []).map((row) => (
                    <tr key={row.childTenantId} className="border-b border-[var(--color-border)]">
                      <td className="p-3 text-[var(--color-surface-text)]">
                        <div className="font-medium">{row.tenantName || '—'}</div>
                      </td>
                      <td className="p-3">{row.openIssuesCount}</td>
                      <td className="p-3">{row.openActionsCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {tab === 'pending' && (
        <div className={panelClass}>
          {pending.length === 0 ? (
            <p className="p-6 md:p-8 text-sm text-[var(--color-secondary-text)]">{t('pePortfolio.pending.empty')}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {pending.map((m) => {
                const orgDisplay =
                  m.childTenantName?.trim() || m.childTenantDomain?.trim() || '—';
                const hasAddress = (m.childHeadquartersLocation?.trim() ?? '') !== '';
                return (
                <li
                  key={m.id}
                  className="flex flex-col gap-4 p-4 md:p-5 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 space-y-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-secondary-text)]">
                        {t('pePortfolio.pending.label.organization')}
                      </p>
                      <p className="text-base font-semibold text-[var(--color-surface-text)] leading-snug mt-0.5">
                        {orgDisplay}
                      </p>
                    </div>
                    {hasAddress && (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-secondary-text)]">
                          {t('pePortfolio.pending.label.address')}
                        </p>
                        <p className="text-sm text-[var(--color-surface-text)] leading-relaxed mt-0.5">
                          {m.childHeadquartersLocation}
                        </p>
                      </div>
                    )}
                    <p className="text-sm text-[var(--color-surface-text)] pt-1 border-t border-[var(--color-border)]">
                      <span className="text-[var(--color-secondary-text)]">{t('pePortfolio.pending.requestedBy')} </span>
                      {m.requesterDisplayName?.trim() || '—'}
                      {m.requesterEmail ? (
                        <span className="text-[var(--color-secondary-text)]"> · {m.requesterEmail}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-[var(--color-secondary-text)]">
                      {t('pePortfolio.pending.peAdminSnapshot')} {m.peAdminEmailSnapshot ?? '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2 sm:pt-0.5">
                    <Button type="button" variant="primary" size="sm" disabled={busy} onClick={() => void onApprove(m)}>
                      {t('pePortfolio.pending.btn.approve')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void onReject(m)}>
                      {t('pePortfolio.pending.btn.reject')}
                    </Button>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {tab === 'companies' && (
        <div className={panelClass}>
          {companies.length === 0 ? (
            <p className="p-6 text-[var(--color-secondary-text)]">{t('pePortfolio.companies.empty')}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {companies.map((m) => (
                <li key={m.id} className="p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-medium text-[var(--color-surface-text)]">
                      {portfolioCompanyDisplayName(m)}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {ws.lifecycle && (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onRemove(m.childTenantId)}
                            className="rounded-md px-3 py-1.5 text-sm border border-[var(--color-border)]"
                          >
                            {t('pePortfolio.companies.remove')}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onSchedule(m.childTenantId)}
                            className="rounded-md px-3 py-1.5 text-sm text-[var(--color-error)] border border-[var(--color-error)]"
                          >
                            {t('pePortfolio.companies.scheduleDelete')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {scheduled.length > 0 && (
            <div className="border-t border-[var(--color-border)] p-4">
              <h3 className="text-sm font-semibold text-[var(--color-surface-text)] mb-3">{t('pePortfolio.scheduled.title')}</h3>
              <ul className="space-y-3">
                {scheduled.map((m) => (
                  <li key={m.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded border border-[var(--color-border)] p-3">
                    <div>
                      <span className="text-sm font-medium text-[var(--color-surface-text)]">
                        {portfolioCompanyDisplayName(m)}
                      </span>
                      {m.hardDeleteOn && (
                        <p className="text-xs text-[var(--color-warning)] mt-1">
                          {t('pePortfolio.scheduled.removalDate', { date: m.hardDeleteOn ?? '—' })}
                        </p>
                      )}
                    </div>
                    {ws.lifecycle && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onPeRevert(m.childTenantId)}
                      className="rounded-md px-3 py-1.5 text-sm text-[var(--color-info)] border border-[var(--color-info)]"
                    >
                      {t('pePortfolio.companies.revertDelete')}
                    </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {tab === 'metrics' && (
        <div className={panelClass + ' p-6'}>
          <p className="text-sm text-[var(--color-secondary-text)] mb-4">{t('pePortfolio.metrics.intro')}</p>
          {metricDefs.length === 0 ? (
            <p className="text-[var(--color-secondary-text)]">{t('pePortfolio.metrics.empty')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {metricDefs.map((d) => (
                <li key={d.id} className="border border-[var(--color-border)] rounded-md p-3">
                  <span className="font-medium text-[var(--color-surface-text)]">{d.name}</span>{' '}
                  <code className="text-xs text-[var(--color-secondary-text)]">{d.code}</code>
                  <span className="text-[var(--color-secondary-text)]"> — {d.operation}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {tab === 'provisioning' && (
        <div className={panelClass + ' p-6'}>
          <PePortfolioProvisioningTab />
        </div>
      )}
      {tab === 'connectors' && connectors && (
        <div className={`${panelClass} p-6`}>
          <p className="text-sm text-[var(--color-surface-text)]">{connectors.message}</p>
          <p className="mt-2 text-xs text-[var(--color-secondary-text)]">
            {t('pePortfolio.connectors.enabledLabel')}{' '}
            {connectors.enabled ? t('pePortfolio.connectors.yes') : t('pePortfolio.connectors.no')}
          </p>
        </div>
      )}
      {tab === 'scopes' && (
        <div className="space-y-4">
          <div className={`${panelClass} p-4`}>
            <p className="text-sm text-[var(--color-secondary-text)] mb-3">{t('pePortfolio.scopes.intro')}</p>
            <p className="text-xs text-[var(--color-secondary-text)] mb-3">
              {t('pePortfolio.scopes.hierarchyHint') ||
                'Use “Hierarchy scope” on a row to limit delegated visibility to a subtree under business, functional, or geographic hierarchies (optional).'}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex-1 block">
                <span className="text-xs font-medium text-[var(--color-secondary-text)]">{t('pePortfolio.scopes.user')}</span>
                <select
                  value={scopePeUserId}
                  onChange={(e) => setScopePeUserId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-surface-bg)]"
                  disabled={busy}
                >
                  <option value="">{t('pePortfolio.scopes.selectUser')}</option>
                  {(scopeOptions?.peUsers ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.firstName || u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : u.email} — {u.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex-1 block">
                <span className="text-xs font-medium text-[var(--color-secondary-text)]">{t('pePortfolio.scopes.company')}</span>
                <select
                  value={scopeChildId}
                  onChange={(e) => setScopeChildId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-surface-bg)]"
                  disabled={busy}
                >
                  <option value="">{t('pePortfolio.scopes.selectCompany')}</option>
                  {(scopeOptions?.companies ?? []).map((c) => (
                    <option key={c.childTenantId} value={c.childTenantId}>
                      {c.tenantName?.trim() || '—'}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onAssignScope()}
                className="rounded-md px-4 py-2 text-sm font-medium text-[var(--color-primary-text)] bg-[var(--color-primary-bg)]"
              >
                {t('pePortfolio.scopes.assign')}
              </button>
            </div>
          </div>
          <div className={panelClass}>
            {scopes.length === 0 ? (
              <p className="p-6 text-[var(--color-secondary-text)]">{t('pePortfolio.scopes.empty')}</p>
            ) : (
              <ul className="divide-y divide-[var(--color-border)]">
                {scopes.map((s) => {
                  const roots = s.pe_scope_data_roots;
                  const hasRoots =
                    roots != null && typeof roots === 'object' && Object.keys(roots as object).length > 0;
                  return (
                    <li key={s.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm text-[var(--color-surface-text)] space-y-1 min-w-0 flex-1">
                        <div>
                          <span className="text-[var(--color-secondary-text)]">{t('pePortfolio.scopes.rowUser')} </span>
                          {s.pe_user_display?.trim() || '—'}
                        </div>
                        <div>
                          <span className="text-[var(--color-secondary-text)]">{t('pePortfolio.scopes.rowCompany')} </span>
                          {s.child_tenant_name?.trim() || '—'}
                        </div>
                        {hasRoots ? (
                          <div className="text-xs text-amber-800 dark:text-amber-200/90">
                            {t('pePortfolio.scopes.hierarchyRestrictedBadge') || 'Hierarchy: restricted subtrees'}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 shrink-0">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setHierarchyModalScope(s)}
                          className="text-sm text-[var(--color-info)] underline"
                        >
                          {t('pePortfolio.scopes.editHierarchyRoots') || 'Hierarchy scope'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onRevokeScope(s.id)}
                          className="text-sm text-[var(--color-error)] underline"
                        >
                          {t('pePortfolio.scopes.revoke')}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <PePortfolioScopeHierarchyModal
            scope={hierarchyModalScope}
            open={hierarchyModalScope != null}
            onClose={() => setHierarchyModalScope(null)}
            onSaved={() => void loadPe()}
            title={t('pePortfolio.scopes.hierarchyModalTitle') || 'Hierarchy data scope'}
            intro={
              t('pePortfolio.scopes.hierarchyModalIntro') ||
              'Choose a root node per hierarchy type to limit what this user can see for that portfolio company when viewing as that company. Leave as “entire hierarchy” for no restriction on that dimension.'
            }
            fullTreeLabel={t('pePortfolio.scopes.fullHierarchy') || 'Entire hierarchy (default)'}
            saveLabel={t('common.save') || 'Save'}
            cancelLabel={t('common.cancel') || 'Cancel'}
            successToast={t('pePortfolio.scopes.hierarchySaved') || 'Hierarchy scope saved'}
          />
        </div>
      )}
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{t('pePortfolio.title')}</h1>
                <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">{subtitle}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="mt-6">
              <p className="text-sm text-[var(--color-secondary-text)]">{t('pePortfolio.loading')}</p>
            </div>
          ) : isPeTenant ? (
            peSection()
          ) : (
            <div className="mt-6">
              <PePortfolioChildTenantPanel />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
